import {createStyles, makeStyles, Paper} from '@material-ui/core';
import {Roles} from 'app/Constants';
import {LookupProps} from 'components/grid-dx/MasterGrid';
import IntegrityRoleGrid from 'components/UserRole';
import {Column, Lookup} from 'devextreme-react/data-grid';
import useLoadLookupCourse from 'features/course/hooks/useLoadLookupCourse';
import {i18n2} from 'features/i18n/i18n';
import useLoadLookupSectors from 'features/sectors/hooks/useLoadLookupSectors';
import React from 'react';
import {IntegrityInstanceGrid} from '.';
import {
  useAddStage,
  useDeleteStage,
  useLoadStage,
  useUpdateStage,
} from '../hooks/index';
import {StageSchema} from '../model/Stage';
import {useUtilInformation} from 'components/hooks';

interface StatusProps {
  status: number;
  text: string;
}

export const lookupStatus = {
  store: {
    type: 'array',
    data: [
      {id: 0, name: i18n2._('Empezada')},
      {id: 1, name: i18n2._('Finalizada')},
      {id: 2, name: i18n2._('Cancelada')},
      {id: 3, name: i18n2._('No Empezada')},
      {id: 4, name: i18n2._('Inactivo')},
    ],
    key: 'id',
  },
};

export const lookupModality = {
  store: {
    type: 'array',
    data: [
      {id: 0, name: i18n2._('Online')},
      {id: 1, name: i18n2._('Tablet')},
      {id: 2, name: i18n2._('InPerson')},
    ],
    key: 'id',
  },
};

export const lookupCapa = {
  store: {
    type: 'array',
    data: [
      {id: 0, name: i18n2._('No aplica')},
      {id: 1, name: i18n2._('Pendiente')},
      {id: 2, name: i18n2._('Terminado')},
    ],
    key: 'id',
  },
};

export const Status = ({status, text}: StatusProps) => {
  const classes = useStyles();
  switch (status) {
    case 4:
      return (
        <Paper className={`grey-background white-text ${classes.statusButton}`}>
          {text}
        </Paper>
      );
    case 3:
      return (
        <Paper className={`red-background white-text ${classes.statusButton}`}>
          {text}
        </Paper>
      );
    case 2:
      return (
        <Paper className={`grey-background white-text ${classes.statusButton}`}>
          {text}
        </Paper>
      );
    case 1:
      return (
        <Paper
          className={`green-background white-text ${classes.statusButton}`}>
          {text}
        </Paper>
      );
    case 0:
      return (
        <Paper
          className={`yellow-background white-text ${classes.statusButton}`}>
          {text}
        </Paper>
      );
    default:
      return (
        <Paper className={`red-background white-text ${classes.statusButton}`}>
          Error
        </Paper>
      );
  }
};

const StageGrid = () => {
  const loadStage = useLoadStage(false);
  const addStage = useAddStage();
  const updateStage = useUpdateStage();
  const deleteStage = useDeleteStage();
  const loadLookupCourses = useLoadLookupCourse(); //Esta diferencia la tenemos que hacer porque cuando cargas los cursos
  const {token, cwid, role} = useUtilInformation();
  const loadLookupSectors = useLoadLookupSectors({token, cwid, role});

  return (
    <IntegrityRoleGrid
      allowEdit={[Roles.AdminITPS, Roles.AdminQA, Roles.AdminHSE]}
      schema={StageSchema(false)}
      initialValues={{duration: 1}}
      dataSource={loadStage}
      promiseAdd={addStage}
      promiseEdit={updateStage}
      promiseDelete={deleteStage}
      withoutEdit={{column: 'status', equals: [1, 2, 4]}}
      id={['iD_Instance', 'iD_Course']}
      remoteLookup={[
        {
          column: 'iD_Course',
          keyValue: 'iD_Course',
          displayValue: 'name',
          dataSource: loadLookupCourses,
        },
        {
          column: 'sector',
          keyValue: 'iD_Sector',
          displayValue: 'name',
          dataSource: loadLookupSectors,
        },
      ]}
      columnOptions={columnOptions}
      withoutDelete={{column: 'status', equals: [1]}}
      integrityGrid={IntegrityInstanceGrid}
      integrityId={['ID_Instance', 'ID_Course']}
      integrityName={'Instance'}
      exportName="Instance-export">
      <Column caption={i18n2._('#Course')} dataField="iD_Course" />
      <Column caption={i18n2._('#Instancia')} dataField="iD_Instance" />
      <Column caption={i18n2._('Nombre del curso')} dataField="course_Name" />
      <Column caption={i18n2._('Modalidad')} dataField="modality">
        <Lookup
          dataSource={lookupModality}
          valueExpr="id"
          displayExpr={(item: LookupProps) => {
            return i18n2._(item ? item.name : '');
          }}
        />
      </Column>
      <Column caption={i18n2._('#Sector')} dataField="sector" visible={false} />
      <Column
        caption={i18n2._('Nombre del sector')}
        dataField="sector_Name"
        defaultSortOrder={'asc'}
      />
      <Column
        caption={i18n2._('Fecha de instancia')}
        dataField="instance_Date"
        dataType="date"
        format="dd/MM/yyyy"
        calculateCellValue={(row: any) => new Date(row.instance_Date)}
      />
      <Column
        caption={i18n2._('Estado')}
        dataField="status"
        width={150}
        cellComponent={(props) => (
          <Status status={props.data.value} text={props.data.text} />
        )}>
        <Lookup
          dataSource={lookupStatus}
          valueExpr="id"
          displayExpr={(item: LookupProps) => {
            return i18n2._(item ? item.name : '');
          }}
        />
      </Column>
      <Column
        caption={i18n2._('Observacion')}
        dataField="observations"
        visible={false}
      />
      <Column caption={i18n2._('Capa inicial')} dataField="capaIniLeg">
        <Lookup
          dataSource={lookupCapa}
          valueExpr="id"
          displayExpr={(item: LookupProps) => {
            return i18n2._(item ? item.name : '');
          }}
        />
      </Column>
      <Column
        caption={i18n2._('Vencimiento de la instancia')}
        dataField="expiration_Date"
        dataType="date"
        format="dd/MM/yyyy"
        calculateCellValue={(row: any) =>
          row.expiration_Date ? new Date(row.expiration_Date) : null
        }
      />
      <Column
        caption={i18n2._('Duracion')}
        visible={false}
        dataField="duration"
      />
      <Column caption={i18n2._('Instructor')} dataField="instructor" />
      <Column caption={i18n2._('Revision')} dataField="revision" />
      <Column caption={i18n2._('Motivo')} dataField="reason" />
      <Column
        caption={i18n2._('Fecha de actualizacion')}
        visible={false}
        dataField="updateDate"
      />
      <Column
        caption={i18n2._('Actualizado por')}
        visible={false}
        dataField="updatedBy"
      />
      <Column caption={i18n2._('Eliminado')} dataField="deleted" width={100} />
    </IntegrityRoleGrid>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    statusButton: {
      paddingTop: '1px',
      paddingBottom: '1px',
      textAlign: 'center',
    },
  }),
);

const columnOptions = [
  {
    column: 'iD_Instance',
    allowEdit: false,
    hiddenAdd: true,
  },
  {
    selectType: 'multipleName',
    column: 'iD_Course',
    lookupColumn: 'version',
    required: true,
    myLabel: i18n2._('Curso'),
    allowEdit: false,
  },
  {
    column: 'sector',
    myLabel: i18n2._('Sector'),
    allowEdit: true,
    required: true,
  },
  {
    column: 'course_Name',
    hiddenEdit: true,
    hiddenAdd: true,
  },
  {
    column: 'sector_Name',
    hiddenEdit: true,
    hiddenAdd: true,
  },
  {
    column: 'status',
    hiddenAdd: true,
    hiddenEdit: true,
  },
  {
    column: 'instructor',
    sizeMobile: 12,
    sizeDesktop: 6,
    required: true,
  },
  {
    column: 'duration',
    myLabel: i18n2._('Duración (hs)'),
    required: true,
  },
  {
    column: 'instance_Date',
    disabledPast: true,
  },
  {
    selectType: 'enum',
    column: 'modality',
    allowEdit: false,
    data: [
      {
        key: 0,
        value: 'Online',
      },
      {
        key: 1,
        value: 'Tablet',
      },
      {
        key: 2,
        value: i18n2._('InPerson'),
      },
    ],
  },
  {
    column: 'capaIniLeg',
    hiddenEdit: true,
    hiddenAdd: true,
  },
  {
    selectType: 'dateByCourse',
    column: 'expiration_Date',
    required: true,
  },
];

export default StageGrid;
